import React from 'react';
import { useGameStore } from '../stores/gameStore';
import { useShardStore } from '../stores/shardStore';
import { formatNumber, computeRaidCost } from '@annopolis/shared';
import { launchRaid, contestNode } from '../game/socket';

export function PvPPanel() {
  const { save, attackPower, defensePower } = useGameStore();
  const { shard, raidCooldowns, activeRaidCount } = useShardStore();

  const influenceBalance = save.resources.pvp;
  const raidCost = computeRaidCost(activeRaidCount);

  return (
    <div style={styles.panel}>
      {/* Power stats */}
      <div style={styles.statsRow}>
        <StatBox label="Attack" value={formatNumber(attackPower)} color="#f87171" icon="⚔" />
        <StatBox label="Defense" value={formatNumber(defensePower)} color="#818cf8" icon="🛡" />
        <StatBox label={save.tier === 1 ? 'Influence' : 'Fleet Cmd'} value={formatNumber(influenceBalance)} color="#fbbf24" icon="◈" />
        <StatBox label="Raid Cost" value={formatNumber(raidCost)} color="#f87171" icon="⚡" />
      </div>

      {/* Node control */}
      {shard && (
        <>
          <div style={styles.sectionHeader}>Control Nodes</div>
          <div style={styles.nodeGrid}>
            {shard.nodes.map((node) => {
              const isOwned = node.ownerId === save.playerId;
              const color = isOwned ? '#4af0c4' : node.ownerId ? '#f87171' : '#4a6080';
              return (
                <button
                  key={node.id}
                  style={{ ...styles.nodeBtn, borderColor: `${color}66`, background: isOwned ? `${color}18` : 'rgba(10,14,26,0.6)' }}
                  title={`Node ${node.id}: ${node.bonusType} (${node.controlScore.toFixed(0)}%)`}
                  onClick={() => contestNode(node.id)}
                >
                  <span style={{ color, fontSize: 9, fontWeight: 700 }}>{node.controlScore.toFixed(0)}</span>
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Leaderboard / targets */}
      {shard && shard.leaderboard.length > 0 && (
        <>
          <div style={styles.sectionHeader}>Shard Leaderboard</div>
          <div style={styles.leaderboard}>
            {shard.leaderboard.slice(0, 15).map((entry, i) => {
              const isMe = entry.playerId === save.playerId;
              const onCd = raidCooldowns.has(entry.playerId) && (raidCooldowns.get(entry.playerId)! > Date.now());
              return (
                <div key={entry.playerId} style={{ ...styles.lbRow, ...(isMe ? styles.lbRowMe : {}) }}>
                  <span style={styles.lbRank}>#{i + 1}</span>
                  <span style={{ ...styles.lbName, color: entry.isOnline ? '#c8d8f0' : '#4a6080' }}>
                    {entry.isOnline ? '● ' : '○ '}{entry.username}
                  </span>
                  <span style={styles.lbScore}>{formatNumber(entry.powerScore)}</span>
                  {!isMe && (
                    <button
                      style={{ ...styles.raidBtn, ...(onCd || influenceBalance < raidCost ? styles.raidBtnDisabled : {}) }}
                      onClick={() => !onCd && influenceBalance >= raidCost && launchRaid(entry.playerId)}
                      disabled={onCd || influenceBalance < raidCost}
                    >
                      {onCd ? 'CD' : 'Raid'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {!shard && (
        <div style={styles.noShard}>
          <p style={styles.noShardText}>Not yet assigned to a shard.</p>
          <p style={styles.noShardSub}>Keep progressing — you'll be matched once you reach a competitive threshold.</p>
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, color, icon }: { label: string; value: string; color: string; icon: string }) {
  return (
    <div style={styles.statBox}>
      <span style={{ ...styles.statIcon, color }}>{icon}</span>
      <span style={styles.statValue}>{value}</span>
      <span style={{ ...styles.statLabel, color: `${color}99` }}>{label}</span>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  panel: { display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto', background: 'rgba(10,14,26,0.98)' },
  statsRow: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 1, padding: '8px 12px', borderBottom: '1px solid rgba(74,240,196,0.1)' },
  statBox: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '6px 4px' },
  statIcon: { fontSize: 14 },
  statValue: { fontSize: 14, fontWeight: 700, color: '#e0e8ff' },
  statLabel: { fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.08em' },
  sectionHeader: { fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, color: '#4af0c4', padding: '8px 12px 4px' },
  nodeGrid: { display: 'flex', flexWrap: 'wrap', gap: 4, padding: '4px 12px', borderBottom: '1px solid rgba(74,240,196,0.1)' },
  nodeBtn: {
    width: 28, height: 28, border: '1px solid', borderRadius: 4,
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  leaderboard: { flex: 1, overflowY: 'auto' },
  lbRow: { display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderBottom: '1px solid rgba(255,255,255,0.03)' },
  lbRowMe: { background: 'rgba(74,240,196,0.06)' },
  lbRank: { fontSize: 10, color: '#4a6080', minWidth: 22 },
  lbName: { fontSize: 12, flex: 1 },
  lbScore: { fontSize: 11, color: '#818cf8' },
  raidBtn: {
    padding: '2px 8px', background: 'rgba(248,113,113,0.15)', border: '1px solid rgba(248,113,113,0.4)',
    borderRadius: 3, color: '#f87171', fontSize: 10, fontWeight: 600, cursor: 'pointer',
  },
  raidBtnDisabled: { background: 'rgba(74,96,128,0.08)', border: '1px solid rgba(74,96,128,0.2)', color: '#3a5070', cursor: 'not-allowed' },
  noShard: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, gap: 8 },
  noShardText: { color: '#8ba0c0', fontSize: 13, fontWeight: 500 },
  noShardSub: { color: '#4a6080', fontSize: 11, textAlign: 'center' },
};
